/**
 * wizardStore.js
 * State Management for the Collage Wizard.
 * 
 * ARCHITECTURE NOTE:
 * - centerImage: The Single Source of Truth from the User (Blob URL).
 * - images: Array[4] of AI Generated Variations (Base64).
 *   - images[0]: The user's primary selection.
 *   - images[1-3]: Random variations.
 */
import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { geminiService } from '../services/GeminiService';
import { resizeImageForAI } from '../logic/imageUtils';

const WIZARD_STATE_VERSION = '1';

export const useWizardStore = defineStore('wizard', {
    state: () => ({
        // SECTION 0: Images
        // images: Array of 4 generated Base64 strings.
        images: [null, null, null, null],
        collectedCount: 0,

        // centerImage: User uploaded main photo (Blob URL)
        centerImage: null,
        originalImage: null, // User's primary image choice (Blob URL)
        fileBlob: null,      // The raw File object
        uploadError: null,   // New Error State

        // SECTION 0.5: Emojis & AI
        selectedEmoji: null,
        activeEmojiId: null, // Renamed from selectedEmojiId to avoid collision with getter
        isGenerating: false,
        isCollageGenerating: false,
        previewImage: null, // Temporary holding for the currently previewed generation

        // SECURITY / BOT PROTECTION
        isAnalyzing: false,
        isHumanVerified: false,

        // RESILIENCE / CIRCUIT BREAKER
        errorCount: 0,
        lastErrorTime: 0,
        circuitOpen: false,
        collageError: null,
        previewError: null,

        // SECTION 1: Theme & Layout
        themePreset: 'Bold',
        layout: 'Square', // 'Square' | 'Landscape'

        // SECTION 2: Appearance
        centerVisible: true,
        borderVisible: true,
        buttonStyle: 'Default (Image)',
        labelAlignment: 'Outer', // 'Outer' | 'Center' | 'Inner'
        labelsVisible: true,
        labelSize: 'M', // 'S' | 'M' | 'L'

        // SECTION 3: Advanced Controls
        spacing: 50,
        circleRadius: 75,
        labelFineTune: 20,
        borderColor: '#000000',
        centerBorderColor: '#ffffff',

        // SECTION 4: Viewport
        zoomLevel: 100,

        // SECTION 5: Workflow & Funnel
        currentStep: 1,
        unlockedStep: 1, // 1: Upload, 2: Analyze(Internal), 3: Emoji, 4: Preview, 5: Collage

        // SECTION 6: External Config (Isolation Law)
        availableAnimals: [],
        availableEmojis: [],
    }),

    getters: {
        // selectedEmojiId normalises selectedEmoji (string or object) to an id string
        // so components can bind :class="selectedId === item.id" without caring about shape
        selectedEmojiId: (state) => {
            if (!state.selectedEmoji) return null;
            if (typeof state.selectedEmoji === 'object') return state.selectedEmoji.id ?? null;
            return state.selectedEmoji;
        },
    },

    actions: {
        setTenantConfig(config) {
            if (!config) return;
            if (config.animals && Array.isArray(config.animals)) {
                this.availableAnimals = config.animals;
            }
            const emojis = config.features?.emojis || config.emojis;
            if (emojis && Array.isArray(emojis)) {
                this.availableEmojis = emojis;
            }
        },

        applyTheme(themeName) {
            this.themePreset = themeName;
            const themeMap = {
                'Vibrant': { border: '#8B5CF6', center: '#ffffff' },
                'Noir': { border: '#000000', center: '#333333' },
                'Wildfire': { border: '#EF4444', center: '#F97316' },
                'Aurora': { border: '#6366F1', center: '#E879F9' },
                'Jungle': { border: '#064E3B', center: '#10B981' }
            };

            const config = themeMap[themeName] || themeMap['Vibrant'];
            this.borderColor = config.border;
            this.centerBorderColor = config.center;
            this.persistState();
        },

        applyLayout(layoutType) {
            this.layout = layoutType;
            // Enforce Layout Math Rules from collage_documentation.html
            if (layoutType === 'Landscape') {
                this.spacing = 15; // 2% relative roughly translates to 15 on our 0-100 scale
                this.circleRadius = 30; // 30% rule
            } else {
                this.spacing = 10; // 1.5% rule
                this.circleRadius = 38; // 38% rule
            }
            this.persistState();
        },

        triggerLogin(method) {
            // New action to replace $emit('login', method) from AuthIsland
            // The actual auth implementation is handled at the application level (e.g., control-plane routers)
            // but the store tracks the *intent* so orchestrators can react.
            console.log(`[WizardStore] Login Intent Triggered: ${method}`);
            // In a full implementation, this might dispatch to a dedicated authStore or trigger a router push.
        },

        setCenterImage(url) {
            // Memory Safety: Revoke previous blob URLs
            [this.centerImage, this.originalImage].forEach(item => {
                if (item && typeof item === 'string' && item.startsWith('blob:')) {
                    URL.revokeObjectURL(item);
                }
            });

            this.centerImage = url;
            this.originalImage = url;
            this.uploadError = null;

            // Critical: Ensure images array maintains contract [null, null, null, null]
            this.images = [null, null, null, null];
        },

        async verifyHumanity(file) {
            this.isAnalyzing = true;
            this.isHumanVerified = false;
            this.uploadError = null;

            try {
                // 1. Resize/Format on Client
                const resizedBase64 = await resizeImageForAI(file);

                // 2. Call Backend API
                const result = await geminiService.analyzeFace(resizedBase64);

                if (result.isPerson) {
                    this.isHumanVerified = true;
                    this.fileBlob = markRaw(file); // markRaw prevents Pinia from proxying File objects
                    // Success: index 0 is our high-res "primary" match (source-like quality)
                    this.images[0] = resizedBase64;

                    console.log(`Human Verified: ${result.reason}`);
                    return true;
                } else {
                    this.uploadError = "No face detected! Please use a clear portrait.";
                    return false;
                }
            } catch (e) {
                console.error("Verification Error", e);
                this.uploadError = "Verification service unavailable. Please try again.";
                return false;
            } finally {
                this.isAnalyzing = false;
            }
        },

        async generateExpression() {
            if (!this.selectedEmoji) {
                this.previewError = "Please select an emoji first!";
                return;
            }
            if (!this.centerImage) {
                this.previewError = "Please upload a photo first!";
                return;
            }

            this.isGenerating = true;
            this.previewImage = null;

            // CIRCUIT BREAKER CHECK
            const CIRCUIT_THRESHOLD = 3;
            // Reset only if errors have occurred and enough time has passed (5 minutes)
            if (this.lastErrorTime > 0 && Date.now() - this.lastErrorTime > 5 * 60 * 1000) {
                this.errorCount = 0;
                this.circuitOpen = false;
            }

            if (this.circuitOpen || this.errorCount >= CIRCUIT_THRESHOLD) {
                console.warn("⚠️ Circuit Breaker OPEN. Using Mock Generation.");
                this.circuitOpen = true;
                const mock = await geminiService.mockGeneration(this.selectedEmoji);
                this.previewImage = mock;
                this.isGenerating = false;
                return;
            }

            try {
                // 1. Prepare Source Image (Always User Center Image)
                const sourceBlob = this.centerImage;

                // 2. Client-Side Resize 
                const resizedBase64 = await resizeImageForAI(sourceBlob);

                const resultBase64 = await geminiService.generatePreview(this.activeEmojiId, this.selectedEmoji, resizedBase64);

                this.previewImage = resultBase64;

                // Success resets the breaker
                this.errorCount = 0;

            } catch (error) {
                console.error("Generation Failed:", error);

                // Increment Circuit Breaker
                this.errorCount++;
                this.lastErrorTime = Date.now();

                this.previewError = error.message.includes("Credits") ? "Insufficient Credits!" : "Failed to generate expression.";
            } finally {
                this.isGenerating = false;
            }
        },

        async generateCollageAssets() {
            // Safety Check
            if (!this.centerImage || !this.images[0]) {
                console.error("Cannot generate collage: Missing source or primary asset");
                return;
            }

            const emptySlots = this.images.filter(img => !img).length;
            if (emptySlots === 0) {
                console.log("Collage is already fully populated.");
                return;
            }

            this.isCollageGenerating = true;
            this.collageError = null;

            try {
                // Prepare Source
                const resizedBase64 = await resizeImageForAI(this.centerImage);

                // Delegate to backend
                const result = await geminiService.generateCollage(this.activeEmojiId, this.selectedEmoji, resizedBase64);

                if (result.images && result.animals) {
                    let resultIdx = 1; // skip null center image at index 0
                    for (let i = 0; i < 4; i++) {
                        // Only fill empty slots with the newly generated collage assets
                        if (!this.images[i] && resultIdx < result.images.length) {
                            this.images[i] = {
                                src: result.images[resultIdx],
                                emoji: '✨',
                                label: result.animals[resultIdx]
                            };
                            resultIdx++;
                        }
                    }
                }

                console.log("Collage Generation Complete. Animals:", result.animals);

            } catch (err) {
                console.error("Collage Generation Error", err);
                if (err.status === 402) {
                    this.collageError = 'Insufficient credits to generate collage.';
                } else {
                    this.collageError = 'Failed to generate collage. Please try again.';
                }
            } finally {
                this.isCollageGenerating = false;
            }
        },

        saveAndTryAnother() {
            if (this.previewImage && this.collectedCount < 4) {
                // Lock in the current preview
                this.images[this.collectedCount] = {
                    src: this.previewImage,
                    emoji: this.selectedEmoji,
                    label: this.activeEmojiId
                };
                this.collectedCount++;
                
                // Clear state for next generation
                this.previewImage = null;
                this.selectedEmoji = null;
                this.activeEmojiId = null;

                if (this.collectedCount >= 4) {
                    // Auto-advance if fully collected
                    this.unlockStep(5);
                    this.generateCollageAssets();
                }
            }
        },

        confirmPreview() {
            if (this.previewImage) {
                // Lock in the current preview if there's room
                if (this.collectedCount < 4) {
                    this.images[this.collectedCount] = {
                        src: this.previewImage,
                        emoji: this.selectedEmoji,
                        label: this.activeEmojiId
                    };
                    this.collectedCount++;
                }

                // Unlock Final Step
                this.unlockStep(5);

                // Trigger the background composition for any remaining slots
                this.generateCollageAssets();
            }
        },

        unlockStep(step) {
            if (step > this.unlockedStep) {
                this.unlockedStep = step;
                // Auto-advance
                this.currentStep = step;
                this.persistState();
            }
        },

        goToStep(step) {
            if (step <= this.unlockedStep) {
                this.currentStep = step;
                this.persistState();
            }
        },

        persistState() {
            // eslint-disable-next-line no-unused-vars
            const { images, centerImage, fileBlob, previewImage, isGenerating, isCollageGenerating, ...settings } = this.$state;
            const stateToSave = {
                v: WIZARD_STATE_VERSION,
                ...settings
            };
            localStorage.setItem('wizard-funnel-state', JSON.stringify(stateToSave));
        },

        hydrate() {
            try {
                const saved = localStorage.getItem('wizard-funnel-state');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    if (parsed.v !== WIZARD_STATE_VERSION) {
                        // Stale schema — discard to prevent corruption
                        localStorage.removeItem('wizard-funnel-state');
                        return;
                    }
                    // Apply saved settings
                    this.$patch(parsed);
                }
            } catch (e) {
                console.warn('Failed to hydrate wizard state', e);
            }
        },

        setEmoji(payload) {
            // Input Normalization: Handle String or Object
            const emoji = payload && typeof payload === 'object' ? payload.emoji : payload;
            const emojiId = payload && typeof payload === 'object' ? payload.id : null;

            this.selectedEmoji = emoji;
            this.activeEmojiId = emojiId || emoji; // Fallback to char if no ID
            // Auto-generate for "Wow" factor
            this.generateExpression();
        },

        randomizeSelection(items) {
            if (!items || items.length === 0) return;
            const randomItem = items[Math.floor(Math.random() * items.length)];
            console.log('[WizardStore] Random Wow Selection:', randomItem);
            this.setEmoji(randomItem);
        },

        async downloadSettings() {
            const {
                images: slots,
                centerImage,
                centerVisible,
                borderVisible,
                circleRadius,
                spacing,
                borderColor,
                centerBorderColor
            } = this;

            const validImages = slots.filter(img => img && img.src);
            if (!validImages.length && !centerImage) return;

            try {
                const WIDTH = 1200;
                const HEIGHT = (this.layout === 'Landscape') ? 675 : 1200;
                const PAD = 40;
                const GAP = (spacing / 100) * 80;
                const CELL_W = (WIDTH - (PAD * 2) - GAP) / 2;
                const CELL_H = (HEIGHT - (PAD * 2) - GAP) / 2;
                const RADIUS = 40;

                const canvas = document.createElement('canvas');
                canvas.width = WIDTH;
                canvas.height = HEIGHT;
                const ctx = canvas.getContext('2d');

                // 1. Background
                ctx.fillStyle = '#f3f4f6';
                ctx.fillRect(0, 0, WIDTH, HEIGHT);

                // Load all images
                const loadImage = (src) => new Promise((resolve) => {
                    if (!src) { resolve(null); return; }
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.onload = () => resolve(img);
                    img.onerror = () => resolve(null);
                    img.src = src;
                });

                const loaded = await Promise.all(slots.map(img => loadImage(img ? img.src : null)));

                // Grid Positions
                const positions = [
                    [PAD, PAD],
                    [PAD + CELL_W + GAP, PAD],
                    [PAD, PAD + CELL_H + GAP],
                    [PAD + CELL_W + GAP, PAD + CELL_H + GAP]
                ];

                // 2. Draw Cells
                loaded.forEach((img, i) => {
                    if (!img) return;
                    const [x, y] = positions[i];

                    ctx.save();
                    ctx.beginPath();
                    if (ctx.roundRect) {
                        ctx.roundRect(x, y, CELL_W, CELL_H, RADIUS);
                    } else {
                        ctx.rect(x, y, CELL_W, CELL_H);
                    }
                    ctx.clip();

                    ctx.drawImage(img, x, y, CELL_W, CELL_H);

                    // Inner Border
                    if (borderVisible) {
                        ctx.strokeStyle = borderColor || '#000000';
                        ctx.lineWidth = 12;
                        ctx.stroke();
                    }

                    ctx.restore();
                });

                // 3. Center Avatar Overlay
                if (centerVisible && centerImage) {
                    const center = await loadImage(centerImage);
                    if (center) {
                        const R = (circleRadius / 100) * (WIDTH * 0.15 * 1.5);
                        const cx = WIDTH / 2;
                        const cy = HEIGHT / 2;

                        ctx.save();
                        ctx.shadowColor = 'rgba(0,0,0,0.2)';
                        ctx.shadowBlur = 40;
                        ctx.shadowOffsetY = 10;

                        ctx.beginPath();
                        ctx.arc(cx, cy, R, 0, Math.PI * 2);
                        ctx.fillStyle = '#ffffff';
                        ctx.fill();
                        ctx.restore();

                        ctx.save();
                        ctx.beginPath();
                        ctx.arc(cx, cy, R, 0, Math.PI * 2);
                        ctx.clip();
                        ctx.drawImage(center, cx - R, cy - R, R * 2, R * 2);
                        ctx.restore();

                        ctx.beginPath();
                        ctx.arc(cx, cy, R, 0, Math.PI * 2);
                        ctx.strokeStyle = centerBorderColor || '#ffffff';
                        ctx.lineWidth = 24;
                        ctx.stroke();
                    }
                }

                // 4. Watermark
                ctx.save();
                ctx.font = '600 18px Inter, sans-serif';
                ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
                ctx.textAlign = 'center';
                ctx.letterSpacing = '4px';
                ctx.fillText('POWERED BY EMOJI.PHOTO', WIDTH / 2, HEIGHT - 14);
                ctx.restore();

                await new Promise((resolve) => {
                    canvas.toBlob((blob) => {
                        if (!blob) { resolve(); return; }
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `emoji-collage-${Date.now()}.png`;
                        a.click();
                        setTimeout(() => URL.revokeObjectURL(url), 100);
                        resolve();
                    }, 'image/png');
                });

            } catch (err) {
                console.error('Download failed:', err);
            }
        },

        resetToDefaults() {
            // Memory Cleanup
            [this.centerImage, this.originalImage].forEach(item => {
                if (item && typeof item === 'string' && item.startsWith('blob:')) {
                    URL.revokeObjectURL(item);
                }
            });

            // Reset Images
            this.images = [null, null, null, null];
            this.collectedCount = 0;
            this.centerImage = null;
            this.originalImage = null;
            this.fileBlob = null;
            this.previewImage = null;
            this.isGenerating = false;
            this.isAnalyzing = false;
            this.isHumanVerified = false;
            this.uploadError = null;

            // Reset Emoji
            this.selectedEmoji = null;
            this.activeEmojiId = null;

            // Reset Error / Loading State
            this.collageError = null;
            this.previewError = null;
            this.isCollageGenerating = false;
            this.errorCount = 0;
            this.lastErrorTime = 0;
            this.circuitOpen = false;

            this.themePreset = 'Bold';
            this.layout = 'Square';
            this.centerVisible = true;
            this.borderVisible = true;
            this.buttonStyle = 'Default (Image)';
            this.labelAlignment = 'Outer';
            this.labelsVisible = true;
            this.labelSize = 'M';
            this.spacing = 50;
            this.circleRadius = 75;
            this.labelFineTune = 20;
            this.borderColor = '#000000';
            this.centerBorderColor = '#ffffff';
            this.zoomLevel = 100;

            this.currentStep = 1;
            this.unlockedStep = 1;
            this.persistState();
        },

        zoomIn() {
            if (this.zoomLevel < 150) this.zoomLevel += 10;
        },

        zoomOut() {
            if (this.zoomLevel > 50) this.zoomLevel -= 10;
        }
    }
});
